import { useCallback, useEffect, useState } from "react";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import { DEFAULT_WHATSAPP_BUSINESS_ACCOUNT_ID } from "../constants";
import { messageTemplatesService } from "../services/messageTemplatesService";
import type { CreateMessageTemplateInput, MessageTemplate } from "../types";
interface UseMessageTemplatesResult {
  templates: MessageTemplate[];
  isLoading: boolean;
  createTemplate: (params: {
    input: CreateMessageTemplateInput;
  }) => Promise<void>;
  updateTemplate: (params: {
    input: MessageTemplate & { headerMediaFile?: File | null };
  }) => Promise<void>;
  deleteTemplate: (params: { templateId: string }) => Promise<void>;
  refreshTemplates: () => Promise<void>;
}

interface UseMessageTemplatesParams {
  whatsappBusinessAccountId?: string;
  phoneNumberId?: string | null;
  organizationId?: string;
  /**
   * Controla quando o hook pode de fato buscar templates.
   *
   * Motivação:
   * - Evitar "double-fetch" (buscar com DEFAULT e depois com WABA real) enquanto
   *   ainda não sabemos se existe endpoint/contrato ativo.
   * - Quando `enabled` é false, o hook NÃO dispara listagem automática.
   *
   * Observação:
   * - Mantemos `true` como padrão para não quebrar fluxos existentes.
   */
  enabled?: boolean;
}

export function useMessageTemplates(
  params: UseMessageTemplatesParams = {},
): UseMessageTemplatesResult {
  const enabled = params.enabled ?? true;

  const resolvedWhatsappBusinessAccountId = (
    params.whatsappBusinessAccountId ?? ""
  ).trim()
    ? (params.whatsappBusinessAccountId ?? "").trim()
    : DEFAULT_WHATSAPP_BUSINESS_ACCOUNT_ID;

  const resolvedPhoneNumberId =
    String(params.phoneNumberId ?? "").trim() ||
    String(import.meta.env.VITE_FLUXZAP_DEFAULT_PHONE_NUMBER_ID ?? "").trim() ||
    "379190331933594";
  const resolvedOrganizationId = String(params.organizationId ?? "").trim() || null;

  const [templates, setTemplates] = useState<MessageTemplate[]>([]);

  const { run: runList, loading: isLoading } = useAsyncTask<
    [],
    MessageTemplate[]
  >({
    task: useCallback(async () => {
      if (!enabled) {
        return [];
      }
      const next = await messageTemplatesService.list({
        whatsappBusinessAccountId: resolvedWhatsappBusinessAccountId,
      });
      setTemplates(next);
      return next;
    }, [enabled, resolvedWhatsappBusinessAccountId]),
  });

  const refreshTemplates = useCallback(async () => {
    await runList();
  }, [runList]);

  const createTemplate = useCallback(
    async ({ input }: { input: CreateMessageTemplateInput }) => {
      if (!enabled) {
        throw new Error(
          "Templates ainda não estão habilitados para carregamento. Aguarde a configuração inicial.",
        );
      }
      await messageTemplatesService.create({
        whatsappBusinessAccountId: resolvedWhatsappBusinessAccountId,
        phoneNumberId: resolvedPhoneNumberId,
        organizationId: resolvedOrganizationId,
        input,
      });
      await runList();
    },
    [enabled, resolvedOrganizationId, runList, resolvedPhoneNumberId, resolvedWhatsappBusinessAccountId],
  );

  const updateTemplate = useCallback(
    async ({
      input,
    }: {
      input: MessageTemplate & { headerMediaFile?: File | null };
    }) => {
      if (!enabled) {
        throw new Error(
          "Templates ainda não estão habilitados para carregamento. Aguarde a configuração inicial.",
        );
      }
      await messageTemplatesService.update({
        whatsappBusinessAccountId: resolvedWhatsappBusinessAccountId,
        phoneNumberId: resolvedPhoneNumberId,
        organizationId: resolvedOrganizationId,
        input,
      });
      await runList();
    },
    [enabled, resolvedOrganizationId, runList, resolvedPhoneNumberId, resolvedWhatsappBusinessAccountId],
  );

  const deleteTemplate = useCallback(
    async ({ templateId }: { templateId: string }) => {
      if (!enabled) {
        throw new Error(
          "Templates ainda não estão habilitados para carregamento. Aguarde a configuração inicial.",
        );
      }
      const template = templates.find((t) => t.id === templateId);
      if (!template) return;

      await messageTemplatesService.remove({
        whatsappBusinessAccountId: resolvedWhatsappBusinessAccountId,
        templateName: template.name,
      });
      await runList();
    },
    [enabled, runList, templates, resolvedWhatsappBusinessAccountId],
  );

  useEffect(() => {
    if (!enabled) return;
    runList();
  }, [enabled, runList]);

  return {
    templates,
    isLoading,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    refreshTemplates,
  };
}
